<script setup>
import { ref } from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { PaperClipIcon, UserIcon } from '@heroicons/vue/20/solid';
import { ChevronRightIcon, MapPinIcon } from '@heroicons/vue/24/outline';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import ContributorModal from './ContributorModal.vue';
import { useGetLocalizedTime } from '@/Composables/localizedDateTime.js';

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
});

const displayedContributor = ref(null);

const breakpoints = useBreakpoints(breakpointsTailwind);

const lgAndGreater = breakpoints.greaterOrEqual('lg');

const showPanel = () => Boolean(props.item.description !== null || (props.item.files && Object.keys(props.item.files).length > 0));
</script>

<template>
    <Disclosure
        v-slot="{ open }"
        as="div"
        class="border border-b-0 border-gray-200 last-of-type:border-b"
        :default-open="lgAndGreater"
    >
        <DisclosureButton
            :disabled="!showPanel()"
            class="w-full flex flex-col p-4 md:p-6 space-y-2 group items-start"
        >
            <div class="text-gray-500">
                <time datetime="{{ useGetLocalizedTime(item.start_time) }}">
                    {{ useGetLocalizedTime(item.start_time) }}
                </time>
            </div>

            <div class="w-full flex justify-between">
                <h3 class="font-semibold text-xl text-left text-deep-teal-700 group-hover:text-deep-teal-900">
                    {{ item.title }}
                </h3>

                <ChevronRightIcon
                    v-if="showPanel()"
                    class="shrink-0 h-6 w-6 text-gray-700 group-hover:text-gray-800 transition-transform"
                    :class="open && 'rotate-90 transform'"
                />
            </div>

            <div
                v-if="item.display_physical_room && item?.physical_room?.display_name"
                class="inline-flex gap-1 text-gray-700"
            >
                <MapPinIcon
                    class="shrink-0 size-6 opacity-75 -ml-1"
                    aria-hidden="true"
                />

                <p class="font-semibold text-base/6">
                    {{ item.physical_room.display_name }}
                </p>
            </div>
        </DisclosureButton>

        <transition
            v-if="showPanel()"
            enter-active-class="transition duration-100 ease"
            enter-from-class="transform opacity-0"
            enter-to-class="transform opacity-100"
            leave-active-class="transition duration-75 ease"
            leave-from-class="transform opacity-100"
            leave-to-class="transform opacity-0"
        >
            <DisclosurePanel class="text-gray-700 px-4 md:px-6 pt-2 pb-4 md:pb-6">
                <div
                    class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] lg:gap-x-6 gap-y-6 lg:gap-y-0 lg:divide-x lg:divide-gray-200"
                >
                    <div class="flex flex-col justify-between space-y-6">
                        <!-- description -->
                        <div
                            class="prose 2xl:prose-lg max-w-none"
                            v-html="item.description"
                        />

                        <!-- files -->
                        <ul
                            v-if="item.files && Object.keys(item.files).length > 0"
                            class="divide-y divide-gray-200 border border-gray-200 rounded-md"
                        >
                            <li
                                v-for="(displayName, href) in item.files"
                                :key="href"
                                class="flex items-center justify-between py-3 px-3 md:px-4 space-x-2"
                            >
                                <span class="inline-flex items-center font-semibold text-sm text-gray-700">
                                    <PaperClipIcon
                                        class="h-5 w-5 text-gray-400 mr-2"
                                        aria-hidden="true"
                                    />

                                    <span class="line-clamp-1">{{ displayName }}</span>
                                </span>

                                <a
                                    class="font-semibold text-sm text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                                    :href
                                    target="_blank"
                                >
                                    {{ $t('eventDetails.actions.downloadFile') }}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <!-- contributors -->
                    <div
                        v-if="item.contributions.length > 0"
                        class="lg:pl-6"
                    >
                        <span class="text-base font-semibold text-gray-700">
                            {{ $t('eventDetails.headline.contributors') }}
                        </span>

                        <ul
                            role="list"
                            class="divide-y divide-gray-200"
                        >
                            <li
                                v-for="(contributor, index) in item.contributions"
                                :key="index"
                            >
                                <button
                                    v-if="contributor.profile"
                                    class="inline-flex items-center space-x-5 p-3 hover:cursor-pointer outline-none group"
                                    @click="displayedContributor = contributor"
                                >
                                    <picture
                                        v-if="contributor.profile.image"
                                        class="shrink-0"
                                    >
                                        <source
                                            :srcset="contributor.profile.image.glide_avatar_image_url_avif"
                                            type="image/avif"
                                        >
                                        <source
                                            :srcset="contributor.profile.image.glide_avatar_image_url_webp"
                                            type="image/webp"
                                        >
                                        <img
                                            class="h-12 w-12 object-cover rounded-full"
                                            :src="contributor.profile.image.glide_avatar_image_url_jpg"
                                            :alt="`${$t('eventDetails.contributor.image.alt')} ${contributor.profile.fullname}`"
                                            loading="lazy"
                                        >
                                    </picture>
                                    <div
                                        v-else
                                        class="flex items-center justify-center shrink-0 h-12 w-12 bg-white rounded-full border"
                                        aria-hidden="true"
                                    >
                                        <UserIcon class="h-8 w-8 text-gray-600" />
                                    </div>

                                    <div class="font-semibold text-sm text-deep-teal-700 leading-tight group-hover:underline group-focus:underline">
                                        {{ contributor.profile.display_name }}
                                    </div>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </DisclosurePanel>
        </transition>
    </Disclosure>

    <ContributorModal
        v-if="displayedContributor"
        :contributor="displayedContributor"
        :show="Boolean(displayedContributor)"
        @closed="displayedContributor = null"
    />
</template>
